import { z } from 'zod';
// ============================================================
// RSVP Schemas
// ============================================================
export const AttendanceStatusSchema = z.enum([
    'ATTENDING',
    'NOT_ATTENDING',
    'MAYBE',
]);
export const MealOptionSchema = z.enum(['ADULT', 'CHILD', 'VEGETARIAN', 'NONE']);
export const RSVPSchema = z.object({
    id: z.string().cuid2(),
    invitationId: z.string().cuid2(),
    guestName: z.string().max(255),
    guestPhone: z.string().max(500).nullable(),
    guestEmail: z.string().max(500).nullable(),
    attendance: AttendanceStatusSchema,
    guestCount: z.number().int().min(1).max(10).default(1),
    mealOption: MealOptionSchema.nullable(),
    message: z.string().nullable(),
    submittedAt: z.date(),
});
// ============================================================
// 개인정보 수집·이용 동의 (PIPA 제15조)
// ============================================================
/** 현재 RSVP 동의 약관 버전. 약관 변경 시 이 값을 올리면 신규 row에 새 버전이 기록됨. */
export const RSVP_PRIVACY_CONSENT_VERSION = '2026-05-08';
/**
 * RSVP 개인정보 수집·이용 동의 안내문 ([자세히보기] 모달 본문).
 * 클라이언트 컨트롤러와 처리방침 모두 이 상수를 참조해 정합 보장.
 */
export const RSVP_PRIVACY_CONSENT_NOTICE = {
    version: RSVP_PRIVACY_CONSENT_VERSION,
    title: '개인정보 수집·이용 동의',
    items: '성함, (선택) 동행인 성함, 참석여부, 식사여부, 전세버스 탑승여부, 전달사항',
    purpose: '결혼식 참석 여부 확인 및 예식 운영',
    retention: '예식 종료 후 90일 또는 청첩장 만료 시까지',
    refusal: '동의를 거부할 권리가 있으며, 거부 시 참석여부 전달이 제한됩니다.',
};
// ============================================================
// 성함 유효성 (FIKET-468)
// ============================================================
// 에디터 토글 settings.rsvpFields.name 이 ON(기본)이면 성함 필수 + 품질 규칙,
// OFF 면 입력칸이 렌더되지 않으므로 검사하지 않고 빈 문자열을 저장한다.
// 규칙은 서버(라우트)와 클라이언트 컨트롤러(client-scripts/rsvp.ts 등 문자열
// 스크립트라 import 불가 → 동일 로직을 인라인)가 함께 따른다. 바꿀 땐 둘 다.
/** 성함 최소 길이(공백 정리 후). 한글 1자·영문 1자는 거부. */
export const RSVP_GUEST_NAME_MIN_LENGTH = 2;
/** 한글 또는 영문 글자 1개 이상 포함 — 숫자·기호만은 거부. */
export const RSVP_GUEST_NAME_LETTER_RE = /[가-힣A-Za-z]/;
export const RSVP_GUEST_NAME_INVALID_MESSAGE = '성함을 2자 이상 정확히 입력해주세요';
/** 앞뒤 공백 제거 + 연속 공백을 1개로. */
export function normalizeRsvpGuestName(name) {
    return name.trim().replace(/\s+/g, ' ');
}
/** 토글 ON 상태에서의 성함 유효성. 입력은 normalize 전 원본이어도 된다. */
export function isValidRsvpGuestName(name) {
    const n = normalizeRsvpGuestName(name);
    return n.length >= RSVP_GUEST_NAME_MIN_LENGTH && RSVP_GUEST_NAME_LETTER_RE.test(n);
}
// ============================================================
// API Request/Response
// ============================================================
export const SubmitRSVPRequestSchema = z.object({
    invitationId: z.string().cuid2(),
    /**
     * 빈 문자열 허용 — 성함 토글 OFF 템플릿은 입력칸이 없다.
     * 필수 여부·2자 규칙은 라우트가 settings.rsvpFields.name 을 보고 isValidRsvpGuestName 으로 판정.
     */
    guestName: z
        .string()
        .max(255, '이름은 255자를 초과할 수 없습니다')
        .transform((v) => normalizeRsvpGuestName(v)),
    guestPhone: z
        .string()
        .regex(/^01[0-9]-?[0-9]{3,4}-?[0-9]{4}$/, '올바른 전화번호를 입력하세요')
        .optional()
        .transform((val) => val?.replace(/-/g, '')),
    guestEmail: z.string().email('올바른 이메일을 입력하세요').optional(),
    attendance: AttendanceStatusSchema,
    guestCount: z
        .number()
        .int()
        .min(1, '최소 1명 이상이어야 합니다')
        .max(10, '최대 10명까지 가능합니다')
        .default(1),
    mealOption: MealOptionSchema.optional(),
    message: z.string().max(500, '메시지는 500자를 초과할 수 없습니다').optional(),
    /** 개인정보 수집·이용 동의 timestamp (ISO 8601). 누락 시 서버가 거부. */
    privacyConsentedAt: z.string().datetime({ message: '개인정보 수집·이용 동의가 필요합니다' }),
    /** 동의한 약관 버전. 누락 시 서버가 현재 버전으로 보강. */
    privacyConsentVersion: z.string().max(32).optional(),
});
export const RSVPResponseSchema = RSVPSchema.omit({
    guestPhone: true,
    guestEmail: true,
}).extend({
    guestPhoneMasked: z.string().optional(),
    guestEmailMasked: z.string().optional(),
});
export const RSVPStatsResponseSchema = z.object({
    total: z.number().int().min(0),
    attending: z.number().int().min(0),
    notAttending: z.number().int().min(0),
    maybe: z.number().int().min(0),
    totalGuests: z.number().int().min(0),
    mealStats: z.object({
        adult: z.number().int().min(0),
        child: z.number().int().min(0),
        vegetarian: z.number().int().min(0),
        none: z.number().int().min(0),
    }),
});
export const RSVPListResponseSchema = z.object({
    rsvps: z.array(RSVPResponseSchema),
    stats: RSVPStatsResponseSchema,
    invitationId: z.string().cuid2(),
});
// ============================================================
// Validation Helpers
// ============================================================
export const maskPhoneNumber = (phone) => {
    const cleaned = phone.replace(/-/g, '');
    if (cleaned.length === 11) {
        return `${cleaned.slice(0, 3)}-****-${cleaned.slice(-4)}`;
    }
    return '***-****-****';
};
export const maskEmail = (email) => {
    const [local, domain] = email.split('@');
    if (!local || !domain)
        return '***@***';
    const maskedLocal = local[0] + '***';
    return `${maskedLocal}@${domain}`;
};
export const isValidKoreanPhone = (phone) => {
    const cleaned = phone.replace(/-/g, '');
    return /^01[0-9]{8,9}$/.test(cleaned);
};
//# sourceMappingURL=rsvp.js.map